import { SVG_CLOSE_FILLED } from '@getgo/chameleon-icons'
import { html, state } from 'lit-element'
import { nothing } from 'lit-html'
import { repeat } from 'lit-html/directives/repeat'
import { getLocationPathname, getLocationSearchParamsByName, getWindow } from '../../../common/dom-helpers'
import { SEARCH_ROUTE } from '../../../common/routes'
import { ShellElement } from '../../../common/shell-element'
import { t } from '../../../directives/translate'
import type { GlobalSearchResult } from '../../../extensions'
import { executeCommand } from '../../../services/namespaces'
import { getGlobalSearchManager } from '../global-search-manager'
import type { GlobalSearchCategoryUI, GlobalSearchColumnUI } from '../global-search-models'
import searchResultsPageStyles from './search-results-page.styles.scss'

export interface CategorySection {
  readonly category: GlobalSearchCategoryUI
  // eslint-disable-next-line functional/prefer-readonly-type
  readonly results: GlobalSearchResult[]
}

export class SearchResultsPage extends ShellElement {
  static readonly tagName = 'goto-search-results-page'
  @state() private searchQuery = ''
  @state() private searchResults: readonly GlobalSearchResult[] = []
  private categoryResults: Map<string, GlobalSearchResult[]> = new Map<string, GlobalSearchResult[]>()
  private categories: readonly GlobalSearchCategoryUI[] = []

  static get styles() {
    return searchResultsPageStyles
  }

  connectedCallback() {
    super.connectedCallback()
    this.categories = getGlobalSearchManager().getCategories()
    this.searchQuery = getLocationSearchParamsByName('query') ?? ''
    getWindow().addEventListener('popstate', this.queryListener)
    this.unsubscribeFunctions.push(() => getWindow().removeEventListener('popstate', this.queryListener))
  }

  private queryListener = () => {
    if (getLocationPathname() === SEARCH_ROUTE) {
      this.searchQuery = getLocationSearchParamsByName('query') ?? ''
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    changedProperties.forEach((oldValue, propName) => {
      if (propName === 'searchQuery') {
        this.executeSearch()
      }
      if (propName === 'searchResults') {
        this.updateCategoryResults()
      }
    })
  }

  private async executeSearch() {
    this.searchResults = await getGlobalSearchManager().searchFor(this.searchQuery)
  }

  private updateCategoryResults() {
    this.categoryResults.clear()
    this.searchResults.forEach(result => {
      const category = getGlobalSearchManager().getCategory(result)
      if (category) {
        const categoryResults = this.categoryResults.get(category.id)
        if (categoryResults) {
          categoryResults.push(result)
        } else {
          this.categoryResults.set(category.id, [result])
        }
      }
    })
    this.requestUpdate()
  }

  private executeDefaultActionCommand(result: GlobalSearchResult) {
    const defaultAction = getGlobalSearchManager().getResultDefaultAction(result, 'list')
    const params = [result, this.searchQuery]
    if (defaultAction?.command) {
      executeCommand(defaultAction.command, [...params])
    }
  }

  private renderTableHeader(columns: readonly GlobalSearchColumnUI[]) {
    return html`<chameleon-data-table-row>
      ${repeat(
        columns,
        column => column.id,
        column =>
          html`<chameleon-data-table-cell class="column-header">${column.displayName}</chameleon-data-table-cell>`,
      )}
    </chameleon-data-table-row>`
  }

  private renderTableBody(results: GlobalSearchResult[], columns: readonly GlobalSearchColumnUI[]) {
    return html`${repeat(
      results,
      result =>
        html`<chameleon-data-table-row
          class="table-body-data-row"
          @click=${() => this.executeDefaultActionCommand(result)}
          >${this.renderDataTableCells(result, columns)}</chameleon-data-table-row
        >`,
    )} `
  }

  private renderDataTableCells(result: GlobalSearchResult, columns: readonly GlobalSearchColumnUI[]) {
    return html`${repeat(
      columns,
      column => column.id,
      column =>
        html`<chameleon-data-table-cell class="table-body-data-cells"
          >${getGlobalSearchManager().getResultFieldValue(result, column.valueField)}</chameleon-data-table-cell
        >`,
    )}`
  }

  private handleClick = () => {
    getWindow().history.back()
  }

  render() {
    // We are only getting one category for phase one. Logic will need to be adapted once we have the possibility of multiple categories
    const results = this.categoryResults.get(this.categories[0].id)
    return results
      ? html`<div class="search-query-heading">
            <chameleon-typography variant="heading-small" class="heading-text"
              >${t('Search results for')} "${this.searchQuery}"</chameleon-typography
            ><chameleon-icon-button variant="neutral" size="small" class="close-button" @click=${this.handleClick}>
              <chameleon-svg>${SVG_CLOSE_FILLED}</chameleon-svg></chameleon-icon-button
            >
          </div>
          <div class="results-table">
            <chameleon-data-table>
              <chameleon-data-table-header
                >${this.renderTableHeader(this.categories[0].columns)}</chameleon-data-table-header
              ><chameleon-data-table-body
                >${this.renderTableBody(results, this.categories[0].columns)}</chameleon-data-table-body
              >
            </chameleon-data-table>
          </div> `
      : nothing //Add empty state here once available from UX
  }
}

declare global {
  interface HTMLElementTagNameMap {
    readonly [SearchResultsPage.tagName]: SearchResultsPage
  }
}
